if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      pendingMediaIndexTarget = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      mediaIndexClickHandler = undefined;

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.mediaIndexClickHandler = this.handleMediaIndexClick.bind(this);
        this.addEventListener('click', this.mediaIndexClickHandler);

        this.initMobileVariantLayout();
        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.removeEventListener('click', this.mediaIndexClickHandler);
        window.removeEventListener('resize', this.mobileVariantResizeHandler);
        window.visualViewport?.removeEventListener('resize', this.mobileVariantResizeHandler);
        this.mobileVariantResizeObserver?.disconnect();
        cancelAnimationFrame(this.mobileVariantLayoutFrame);
      }

      get usesMobileVariantLayout() {
        return this.id.startsWith('MainProduct-') && window.matchMedia('(max-width: 749px)').matches &&
          !!this.variantSelectors && !!this.querySelector('media-gallery');
      }

      initMobileVariantLayout() {
        if (!this.id.startsWith('MainProduct-')) return;
        this.mobileVariantResizeHandler = () => {
          cancelAnimationFrame(this.mobileVariantLayoutFrame);
          this.mobileVariantLayoutFrame = requestAnimationFrame(() => this.fitMobileVariantMedia());
        };
        window.addEventListener('resize', this.mobileVariantResizeHandler, { passive: true });
        window.visualViewport?.addEventListener('resize', this.mobileVariantResizeHandler, { passive: true });
        this.mobileVariantResizeObserver = new ResizeObserver(this.mobileVariantResizeHandler);
        this.observeMobileVariantPicker();
        this.mobileVariantResizeHandler();
      }

      observeMobileVariantPicker() {
        this.mobileVariantResizeObserver?.disconnect();
        if (this.variantSelectors) this.mobileVariantResizeObserver?.observe(this.variantSelectors);
      }

      previewMobileVariantMedia(target) {
        const version = this.mobilePreviewVersion = (this.mobilePreviewVersion || 0) + 1;
        if (!this.usesMobileVariantLayout ||
          this.variantSelectors.querySelectorAll('[data-ls-option-position]').length !== 1) return;
        const mediaId = target.dataset.variantMediaId;
        const gallery = this.querySelector('media-gallery');
        const image = gallery.querySelector(`[data-media-id="${mediaId}"] .product__media img`);
        if (!image) return;
        const activate = () => {
          if (version !== this.mobilePreviewVersion || !gallery.isConnected) return;
          gallery.setActiveMedia(mediaId, false, true);
        };
        image.loading = 'eager';
        image.fetchPriority = 'high';
        if (image.complete && image.naturalWidth) activate();
        else image.decode().then(activate).catch(() => {});
      }

      fitMobileVariantMedia() {
        if (!this.usesMobileVariantLayout) {
          this.style.removeProperty('--ls-mobile-variant-media-height');
          if (this.pendingDesktopMediaGallery && !window.matchMedia('(max-width: 749px)').matches) {
            this.querySelector('media-gallery')?.replaceWith(this.pendingDesktopMediaGallery);
            this.pendingDesktopMediaGallery = null;
          }
          return;
        }
        const gallery = this.querySelector('media-gallery');
        const active = gallery.querySelector('.product__media-item.is-active');
        if (!active) return;
        const picker = this.variantSelectors;
        const currentHeight = window.visualViewport?.height || window.innerHeight;
        const width = Math.round(gallery.getBoundingClientRect().width);
        if (this.mobileViewportWidth !== width || !this.mobileViewportHeight ||
          Math.abs(currentHeight - this.mobileViewportHeight) > 120) {
          this.mobileViewportWidth = width;
          this.mobileViewportHeight = currentHeight;
        }
        const viewportHeight = this.mobileViewportHeight;
        const headerHeight = Array.from(document.querySelectorAll('.section-header, .ls-trust-bar, .ls-promo-bar-shell'))
          .reduce((height, element) => height + element.getBoundingClientRect().height, 0);
        const galleryExtras = Math.max(0, gallery.getBoundingClientRect().height - active.getBoundingClientRect().height);
        const height = Math.floor(Math.max(180, Math.min(gallery.getBoundingClientRect().width,
          viewportHeight - headerHeight - galleryExtras - picker.getBoundingClientRect().height - 40)));
        if (this.style.getPropertyValue('--ls-mobile-variant-media-height') !== `${height}px`) {
          this.style.setProperty('--ls-mobile-variant-media-height', `${height}px`);
        }
      }

      keepMobileVariantMediaVisible() {
        if (!this.usesMobileVariantLayout) return false;
        const active = this.querySelector('media-gallery .product__media-item.is-active');
        if (!active) return false;
        const headerHeight = document.querySelector('.section-header')?.getBoundingClientRect().height || 0;
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const topLimit = active.getBoundingClientRect().top - headerHeight - 8;
        const bottomLimit = this.variantSelectors.getBoundingClientRect().bottom - viewportHeight + 16;
        const delta = topLimit < 0 ? topLimit : Math.max(0, bottomLimit);
        if (Math.abs(delta) > 8) {
          window.scrollTo({
            top: Math.max(0, window.scrollY + delta),
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
          });
        }
        return true;
      }

      handleMediaIndexClick(event) {
        const trigger = event.target.closest('[data-ls-media-index-target]');
        if (!trigger || !this.contains(trigger)) return;

        const mediaId = trigger.dataset.lsMediaIndexTarget;
        if (!mediaId) return;

        if (this.selectMediaIndexVariant(trigger)) {
          this.pendingMediaIndexTarget = mediaId;
          return;
        }

        this.productModal?.show(trigger);
      }

      selectMediaIndexVariant(trigger) {
        const variantSelectors = this.variantSelectors;
        if (!variantSelectors) return false;

        let changed = false;
        let dispatchTarget = null;

        for (let position = 1; position <= 3; position += 1) {
          const value = trigger.getAttribute(`data-ls-media-option-${position}`);
          if (!value) continue;

          const group = variantSelectors.querySelector(`[data-ls-option-position="${position}"]`);
          if (!group) continue;

          const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find(
            (input) => input.value === value
          );
          const select = group.querySelector('select');

          if (radio && !radio.checked) {
            radio.checked = true;
            changed = true;
            dispatchTarget = radio;
          } else if (select && select.value !== value) {
            select.value = value;
            changed = true;
            dispatchTarget = select;
          }
        }

        if (dispatchTarget) dispatchTarget.dispatchEvent(new Event('change', { bubbles: true }));
        return changed;
      }

      openPendingMediaIndexTarget() {
        const mediaId = this.pendingMediaIndexTarget;
        if (!mediaId) return;

        this.pendingMediaIndexTarget = null;
        const trigger = Array.from(this.querySelectorAll('[data-ls-media-index-target]')).find(
          (button) => button.dataset.lsMediaIndexTarget === mediaId
        );
        if (!trigger) return;

        requestAnimationFrame(() => this.productModal?.show(trigger));
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        if (!shouldSwapProduct) this.previewMobileVariantMedia(target);

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        const controller = this.abortController = new AbortController();

        fetch(requestUrl, { signal: controller.signal })
          .then((response) => response.text())
          .then((responseText) => {
            if (controller.signal.aborted) return;
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus({ preventScroll: this.usesMobileVariantLayout });
          })
          .catch((error) => {
            if (controller.signal.aborted) return;
            this.mobilePreviewVersion = (this.mobilePreviewVersion || 0) + 1;
            const confirmedVariant = this.getSelectedVariant(this);
            const confirmedMedia = confirmedVariant?.featured_media?.id;
            if (this.usesMobileVariantLayout && confirmedMedia) {
              this.querySelector('media-gallery')?.setActiveMedia(`${this.dataset.section}-${confirmedMedia}`, false, true);
              if (confirmedVariant.options.length === 1) {
                const picker = this.variantSelectors;
                const input = Array.from(picker.querySelectorAll('input[type=radio]'))
                  .find((option) => option.value === confirmedVariant.options[0]);
                const select = picker.querySelector('select');
                if (input) input.checked = true;
                if (select) select.value = confirmedVariant.options[0];
                if (input || select) picker.updateSelectionMetadata({ target: input || select });
              }
              this.productForm?.handleErrorMessage(window.cartStrings?.error);
            }
            this.pendingMediaIndexTarget = null;
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
            this.variantSelectors?.rejectPendingSelectPromise(error);
          });
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
          this.observeMobileVariantPicker();
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery');
        const mediaGalleryDestination = html.querySelector('media-gallery');
        const mediaId = `${this.dataset.section}-${variantFeaturedMediaId}`;
        const canReuseMobileGallery = this.usesMobileVariantLayout &&
          mediaGallerySource?.querySelector(`[data-media-id="${mediaId}"]`);
        this.mobilePreviewVersion = (this.mobilePreviewVersion || 0) + 1;
        if (canReuseMobileGallery) {
          this.pendingDesktopMediaGallery = mediaGalleryDestination;
          if (mediaGallerySource.querySelector('.product__media-item.is-active')?.dataset.mediaId !== mediaId) {
            mediaGallerySource.setActiveMedia(mediaId, false, true);
          }
        } else {
          if (mediaGallerySource && mediaGalleryDestination) mediaGallerySource.replaceWith(mediaGalleryDestination);
          this.pendingDesktopMediaGallery = null;
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          this.querySelector('media-gallery')?.setActiveMedia?.(mediaId, false);
        }

        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
        this.openPendingMediaIndexTarget();
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
